/**
 * WHERE THE SONNER ENGINE IS HELD, AND WHY IT IS HELD RATHER THAN IMPORTED.
 *
 * `sonner` is loaded lazily: `../Toaster.tsx` mounts `./SonnerToaster` through
 * `React.lazy`, and that file is the ONE door to the package (asserted by
 * `../__tests__/toast-lazy-engine.test.ts`). A customer on "Old design" — the
 * shipped default — therefore downloads none of it, exactly as a chart-less
 * view downloads neither chart engine (`src/charts`) and an antd Customers
 * list downloads no TanStack.
 *
 * But `message.error(...)` is IMPERATIVE and synchronous: it cannot await a
 * chunk. So the door publishes the resolved `toast` here on import, and
 * `../index.ts` asks `getSonnerToast()` at call time — non-null only once the
 * chunk has landed. Until then (the brief window between app boot and the
 * Toaster's chunk resolving) the seam falls through to the antd notice, the
 * same degradation `../index.ts`'s bridge already uses: a toast may wear the
 * old design for a moment, but no toast is ever DROPPED. An error the user
 * needed to read is not something to lose to a chunk race.
 *
 * The type is `typeof import('sonner').toast` in TYPE position only: erased
 * before the bundler sees it, so it pulls nothing into any chunk, and it keeps
 * the adapter honest against the real package rather than a hand-copied shape.
 */
export type SonnerToast = typeof import('sonner').toast

/** Sonner's per-toast option bag (`ExternalToast`), taken from the package. */
export type SonnerToastOptions = NonNullable<Parameters<SonnerToast>[1]>

/** What Sonner hands back for a live toast, and what `dismiss` takes. */
export type SonnerToastId = ReturnType<SonnerToast>

let engine: SonnerToast | null = null

/**
 * Published by `./SonnerToaster` on import, and only by it.
 *
 * The Toaster does NOT un-publish when it unmounts (a flip back to "Old
 * design"): the chunk is already paid for, and `../index.ts` gates on the
 * design mode anyway, so keeping it avoids a re-flip racing a second load.
 * `null` exists for the tests, which reset between cases.
 */
export const setSonnerToast = (toast: SonnerToast | null): void => {
  engine = toast
}

/** The loaded Sonner entry point, or `null` while its chunk is still in flight. */
export const getSonnerToast = (): SonnerToast | null => engine
